import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { ModalName, ModalService } from '../../core/services/modal.service';
import { SmsOfferCoupon } from '../../core/models/sms-offer-coupon';
import { SmsSignUpInformationComponent } from '../sms-sign-up-information/sms-sign-up-information.component';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { InputComponent } from '../input/input.component';
import { IconComponent } from '../icons/icon.component';
import { GenericObject } from '../../core/models/core/generic-object';
import { OfferDetailsComponent } from './offer-details/offer-details.component';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { MaskFieldInputComponent } from '../mask-field-input/mask-field-input.component';
import { MaskFieldInputType } from '../mask-field-input/mask-field-input-type.enum';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { Subscription } from 'rxjs';
import { ChannelName } from '../../core/models/account/channel-name.enum';
import { SmsModalServie } from '../../core/services/sms-modal.service';
import { Utils } from '../../core/utils/utils';
import { ErrorsService } from '../../core/services/errors.service';
import { AuditService } from '../../core/services/audit.service';
import { AriaFormDirective } from '../directives/aria-form.directive';

@Component({
  selector: 'cwo-sms-offer-modal',
  standalone: true,
  imports: [
    CommonModule,
    RoundIconComponent,
    SmsSignUpInformationComponent,
    IconComponent,
    TranslocoDirective,
    ReactiveFormsModule,
    InputComponent,
    OfferDetailsComponent,
    AsyncButtonDirective,
    MaskFieldInputComponent,
    CheckboxComponent,
    AriaFormDirective
  ],
  templateUrl: './sms-offer-modal.component.html',
  styleUrl: './sms-offer-modal.component.scss',
})
export class SmsOfferModalComponent{
  modalService = inject(ModalService);
  translocoService = inject(TranslocoService);
  authService = inject(AuthService);
  smsModalService = inject(SmsModalServie);
  accountService = inject(AuthService);
  errorService = inject(ErrorsService);
  auditService = inject(AuditService)
  Object = Object;
  account = computed(() => this.authService.state().account);
  isAccountInstantiated = computed(() => this.account()?.email);
  iconNameType = IconNameType;
  maskFieldInputType = MaskFieldInputType;
  smsOfferCoupon: SmsOfferCoupon;
  loadingSub: Subscription | null = null;

  form: FormGroup;
  showErrors = false;
  serverErrors: GenericObject<string[]> = {};

  validationMessages: GenericObject<GenericObject<string>> = {
    name: {
      required: this.translocoService.translate(
        'modals.sms_sub_promo_modal.name_required'
      ),
    },
    phone: {
      required: this.translocoService.translate(
        'modals.sms_sub_promo_modal.phone_required'
      ),
      minlength: this.translocoService.translate(
        'modals.sms_sub_promo_modal.length_error'
      ),
      maxlength: this.translocoService.translate(
        'modals.sms_sub_promo_modal.length_error'
      ),
    },
    email: {
      required: this.translocoService.translate(
        'modals.sms_sub_promo_modal.email_required',
      ),
      email: this.translocoService.translate(
        'modals.sms_sub_promo_modal.email_invalid'
      ),
    },
  };

  constructor() {
    this.smsOfferCoupon = this.modalService.stateForModal(ModalName.SMSOffer)
      ?.coupon as SmsOfferCoupon;
    this.form = new FormGroup({
      phone: new FormControl(
      this.account()?.phone ? this.account()?.phone : '',
      Validators.compose([
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
      ])
    ),
      emailSubscription: new FormControl(true),
    });
    if (!this.isAccountInstantiated()) {
      this.form.addControl('name', new FormControl('', Validators.required));
      this.form.addControl(
        'email',
        new FormControl(
          '',
          Validators.compose([Validators.required, Validators.email])
        )
      );
    }
    if (this.isAccountInstantiated() && !this.account()?.phone) {
      this.form.addControl(
        'password',
        new FormControl(
          '',
          Validators.compose([
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(64),
          ])
        )
      );
    }

    this.form.valueChanges.subscribe(() => {
      this.serverErrors = {};
    });

  }

  get name() {
    return this.form.controls?.name;
  }
  get email() {
    return this.form.controls.email;
  }
  get phone() {
    return this.form.controls.phone;
  }

  get password() {
    return this.form.controls.password;
  }

  get emailSubscription() {
    return this.form.controls.emailSubscription;
  }

  closeModal() {
    this.modalService.closeModal(ModalName.SMSOffer);
  }

  noThanks() {
    this.closeModal();
  }

  subscribe() {
    this.auditService.createAudit(() => 
      'SMS modal subscribe button clicked'
  );
  this.showErrors = true;
  this.serverErrors = {};
  if (!this.form.valid) {
    this.auditService.sendNativeFormErrorsToAudits(this.form, this.validationMessages, 'SMS modal');
      return;
    }

    if (this.isAccountInstantiated()) {
      if (!this.account()?.phone) {
        this.updateAccountsPhone();
      } else {
        this.subscribeInstansiatedUser();
      }
    } else {
      this.subscribeGuestUser();
    }
  }

  private updateAccountsPhone(): void {
     const newPhone = this.phone.value?.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') ?? '';
    const updatedAccount = this.account()?.deserialize({
      phone: newPhone,
      password: this.password.value,
    });
    this.loadingSub = this.accountService
      .updateAccount(updatedAccount!)
      .subscribe({
        next: () => {
          this.subscribeInstansiatedUser();
        },
        error: (error) => {
          this.loadingSub = null;
          this.handleError(error);
        },
      });
  }

  private subscribeInstansiatedUser(): void {
    let updatedSubscriptions = [];
    if (
      this.account()?.subscriptions.findIndex(
        (sub) => sub.channelName === ChannelName.SMS
      ) === -1
    ) {
      //Channel doesn't exist
      updatedSubscriptions = this.smsModalService.addOffersService(
        this.smsModalService.addSmsChannel([...this.account()!.subscriptions])
      );
    } else {
      updatedSubscriptions = this.smsModalService.addOffersService([
        ...this.account()!.subscriptions,
      ]);
    }

    if (this.emailSubscription?.value) {
      updatedSubscriptions = this.smsModalService.addEmailOffers(updatedSubscriptions);
    }

    this.loadingSub = this.accountService
      .updateCustomerSubscriptions(updatedSubscriptions)
      .subscribe(() => {
        this.loadingSub = null;
        this.closeModal();
      });
  }

  private subscribeGuestUser(): void {
    let subs = this.smsModalService.addOffersService(
      this.smsModalService.addSmsChannel([])
    );

    if (this.emailSubscription.value && this.email.value) {
      subs = this.smsModalService.addEmailChannel(subs);
      subs = this.smsModalService.addEmailOffersService(subs);
    }

    const formatedPhone = Utils.formatPhoneWithDash(this.phone.value);

    const fullName = this.name.value;

    this.loadingSub = this.accountService
      .updateGuestCustomerSubcriptions(
        subs,
        fullName,
        this.email.value,
        formatedPhone
      )
      .subscribe({
        next: () => {
          this.loadingSub = null;
          this.closeModal();
        },
        error: (error) => {
          this.handleError(error);
          this.loadingSub = null;
        },
      });
  }

  private handleError(err: any) {
    if (err.status === 409 && err.error && err.error.message === 'It seems that you are already subscribed for SMS') {
      this.serverErrors = { generic: [err.error.message] };
      return;
    }
    if (err.status === 401 || err.status === 409) {
      if (err.error.message.includes('Password')) {
        this.serverErrors = { password: [err.error.message] };
      } else if (err.error.message.includes('phone')) {
        this.serverErrors = { phone: [err.error.message] };
      }
    } else {
      const parsedError = this.errorService.parseErrorResponse(err);
      if (parsedError && parsedError.errors?.cellPhone) {
        parsedError.errors['phone'] = parsedError.errors['cellPhone'];
      }
      if(parsedError && parsedError.errors){
        this.serverErrors = parsedError?.errors;
      }
    }
  }
}